'use strict';

/**
 * SliderGridSwipe
 *
 * Responsive slider that:
 *  - Desktop (>= 841px): renders as a CSS grid with N columns (data-column, default 3).
 *  - Mobile  (<  841px): becomes a Swiper instance with touch swipe only (no arrows, no scrollbar).
 *
 * Activated on elements: <div class="swiper" data-navigation="grid-mobile-swipe" data-column="3">
 *
 * The Swiper instance is destroyed / re-initialized when the viewport crosses
 * the 841px breakpoint.
 */

import Swiper, { A11y } from 'swiper';

import 'swiper/css';
import 'swiper/css/a11y';

const BREAKPOINT = 841; // desktop kicks in at >= 841px
const DEFAULT_COLUMNS = 3;

const SliderGridSwipe = {
    instances: [],
    mediaQuery: null,

    init: function () {
        const els = document.querySelectorAll(
            '.swiper[data-navigation="grid-mobile-swipe"]',
        );

        if (!els || els.length === 0) {
            return;
        }

        els.forEach((el) => {
            const columns = parseInt(el.dataset.column, 10) || DEFAULT_COLUMNS;
            const instance = {
                el: el,
                columns: columns,
                swiper: null,
            };
            SliderGridSwipe.instances.push(instance);
            SliderGridSwipe.applyMode(instance);
        });

        // Use matchMedia change event so we only react when crossing the breakpoint.
        SliderGridSwipe.mediaQuery = window.matchMedia(
            '(max-width: ' + (BREAKPOINT - 1) + 'px)',
        );

        const onChange = () => {
            SliderGridSwipe.instances.forEach(SliderGridSwipe.applyMode);
        };

        if (typeof SliderGridSwipe.mediaQuery.addEventListener === 'function') {
            SliderGridSwipe.mediaQuery.addEventListener('change', onChange);
        } else if (
            typeof SliderGridSwipe.mediaQuery.addListener === 'function'
        ) {
            // Safari < 14 fallback
            SliderGridSwipe.mediaQuery.addListener(onChange);
        }
    },

    applyMode: function (instance) {
        const isMobile = SliderGridSwipe.mediaQuery
            ? SliderGridSwipe.mediaQuery.matches
            : window.matchMedia('(max-width: ' + (BREAKPOINT - 1) + 'px)')
                  .matches;

        if (isMobile) {
            SliderGridSwipe.enableSwipe(instance);
        } else {
            SliderGridSwipe.enableGrid(instance);
        }
    },

    enableSwipe: function (instance) {
        if (instance.swiper) {
            return;
        }

        // Reset any inline grid styles we set in desktop mode so Swiper can take over.
        const wrapper = instance.el.querySelector('.swiper-wrapper');

        if (wrapper) {
            wrapper.style.display = '';
            wrapper.style.gridTemplateColumns = '';
        }

        instance.el.classList.remove('is-grid');
        instance.el.classList.add('is-swipe');

        instance.swiper = new Swiper(instance.el, {
            modules: [A11y],
            a11y: true,
            spaceBetween: 16,
            slidesPerView: 'auto',
            loop: false,
            speed: 600,
            allowTouchMove: true,
        });
    },

    enableGrid: function (instance) {
        if (instance.swiper) {
            instance.swiper.destroy(true, true);
            instance.swiper = null;
        }

        instance.el.classList.remove('is-swipe');
        instance.el.classList.add('is-grid');

        const wrapper = instance.el.querySelector('.swiper-wrapper');

        if (wrapper) {
            wrapper.style.display = 'grid';
            wrapper.style.gridTemplateColumns =
                'repeat(' + instance.columns + ', 1fr)';
        }
    },
};

export default SliderGridSwipe;
